import ReactDOM from 'react-dom/client'
import {
  PublicClientApplication,
  EventType,
  EventMessage,
  AuthenticationResult,
} from '@azure/msal-browser'
import { clearAuthRedirectInProgress } from 'app/services/utils/authRedirect'
import { msalConfig } from 'authConfig'
import App from './app/App'
import './index.css'

// import reportWebVitals from './reportWebVitals'

export const msalInstance = new PublicClientApplication(msalConfig)

if (process.env.NODE_ENV === 'development') {
  const resizeObserverNoiseTokens = [
    'ResizeObserver loop completed with undelivered notifications',
    'ResizeObserver loop limit exceeded',
  ]

  const getMessageFromUnknown = (value: unknown): string => {
    if (typeof value === 'string') return value
    if (value instanceof Error) return value.message || ''

    if (value && typeof value === 'object') {
      const candidate = value as {
        message?: unknown
        error?: unknown
        reason?: unknown
      }

      if (typeof candidate.message === 'string') return candidate.message

      if (candidate.error instanceof Error) {
        return candidate.error.message || ''
      }

      if (typeof candidate.error === 'string') {
        return candidate.error
      }

      if (candidate.reason instanceof Error) {
        return candidate.reason.message || ''
      }

      if (typeof candidate.reason === 'string') {
        return candidate.reason
      }
    }

    return ''
  }

  const isResizeObserverNoise = (value: unknown) => {
    const message = getMessageFromUnknown(value)

    if (!message) return false

    return resizeObserverNoiseTokens.some((token) => message.includes(token))
  }

  const previousOnError = window.onerror

  const handleWindowError = (event: ErrorEvent) => {
    if (isResizeObserverNoise(event.message)) {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }

  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reasonMessage =
      typeof event.reason === 'string'
        ? event.reason
        : event.reason?.message || ''

    if (isResizeObserverNoise(reasonMessage)) {
      event.preventDefault()
    }
  }

  const previousConsoleError = console.error

  console.error = (...args) => {
    if (args.some((arg) => isResizeObserverNoise(arg))) {
      return
    }

    previousConsoleError(...args)
  }

  window.onerror = function (message, source, lineno, colno, error) {
    const normalizedMessage = getMessageFromUnknown(message)

    if (
      isResizeObserverNoise(normalizedMessage) ||
      isResizeObserverNoise(error)
    ) {
      return true
    }

    if (typeof previousOnError === 'function') {
      return previousOnError(message, source, lineno, colno, error)
    }

    return false
  }

  window.addEventListener('error', handleWindowError, true)
  window.addEventListener('unhandledrejection', handleUnhandledRejection)
}

msalInstance
  .initialize()
  .then(() => {
    // Default to using the first account if no account is active on page load
    if (
      !msalInstance.getActiveAccount() &&
      msalInstance.getAllAccounts().length > 0
    ) {
      // Account selection logic is app dependent. Adjust as needed for different use cases.
      msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0])
    }

    // Optional - This will update account state if a user signs in from another tab or window
    msalInstance.enableAccountStorageEvents()

    msalInstance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult
        const { account } = payload
        msalInstance.setActiveAccount(account)
        sessionStorage.setItem('vfuToken', payload.accessToken)
      }
    })
  })
  .then(() => msalInstance.handleRedirectPromise())
  .then(() => {
    clearAuthRedirectInProgress()
    const rootElement = document.getElementById('root') as HTMLElement
    const root = ReactDOM.createRoot(rootElement)
    // @ts-ignore
    root.render(<App msalInstance={msalInstance} />)
  })

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals()
